import React, { useMemo, useState, useEffect, useRef } from "react";
import {
  motion,
  useReducedMotion,
  useScroll,
  useTransform,
  useSpring,
  useMotionValue,
  AnimatePresence,
} from "framer-motion";
import heroSection from "../assets/wedding-hero.jpg";
import {
  CalendarDays,
  CalendarPlus,
  Clock,
  MapPin,
  Church,
  PartyPopper,
  Utensils,
  Ban,
  Phone,
  Send,
  Heart,
  Sparkles,
  Music,
  GlassWater,
  Car,
  ChevronDown,
} from "lucide-react";

/* EVENT TIMING
   Egypt observes DST (UTC+3) until the last Thursday of October,
   so 10 Oct 2026 at 5:00 PM Cairo time is +03:00 (the old +02:00
   made the countdown run an hour late). Adjust endISO if the party
   ends at a different time. */
const EVENT = {
  startISO: "2026-10-10T17:00:00+03:00",
  endISO: "2026-10-11T00:00:00+03:00",
};

/* DATA */
const WEDDING = {
  couple: {
    groom: { en: "Youssef", ar: "يوسف" },
    bride: { en: "Sandra", ar: "ساندرا" },
  },
  date: { en: "10 / 10 / 2026", ar: "١٠ / ١٠ / ٢٠٢٦" },
  dateLong: { en: "October Tenth, Twenty Twenty-Six", ar: "العاشر من أكتوبر، ألفان وستة وعشرون" },
  dayLabel: { en: "Saturday", ar: "السبت" },
  heroSubtitle: { en: "Wedding Invitation", ar: "دعوة زفاف" },
  aya: {
    en: "My beloved is mine, and I am his.\n— Song of Solomon 2:16",
    ar: "حبيبي لي وأنا له.\n— نشيد الأنشاد ٢:١٦",
  },
  calendar: {
    cal: { en: "Save the Date", ar: "احفظ التاريخ" },
    month: { en: "October 2026", ar: "أكتوبر ٢٠٢٦" },
    days: {
      en: ["S", "M", "T", "W", "T", "F", "S"],
      ar: ["ح", "ن", "ث", "ر", "خ", "ج", "س"],
    },
  },
  calendarCta: {
    google: { en: "Google Calendar", ar: "تقويم جوجل" },
    ics: { en: "Apple / Outlook", ar: "آبل / آوتلوك" },
  },
  sections: {
    dayPlan: { en: "The Day", ar: "اليوم" },
    timeline: { en: "Schedule", ar: "البرنامج" },
    dressCode: { en: "Dress Code", ar: "الزي" },
    restrictions: { en: "Kindly Note", ar: "تنبيهات" },
    inclusions: { en: "What to Expect", ar: "المشمول" },
    contact: { en: "CONTACTS", ar: "التواصل" },
  },
  timelineLabels: {
    ceremony: { en: "Ceremony", ar: "المراسم" },
    photos: { en: "Photos & Congratulations", ar: "صور وتهاني" },
    insideChurch: { en: "Inside the church", ar: "داخل الكنيسة" },
    celebration: { en: "Celebration", ar: "الاحتفال" },
    end: { en: "Celebration End", ar: "نهاية الاحتفال" },
  },
  celebrationEndTime: "12:00 AM", // placeholder — adjust to the real end time
  dressCode: { en: "Elegant / Formal", ar: "كلاسيك / رسمي" },
  dressHint: {
    en: "Soft, romantic tones look amazing — but bring your own style.",
    ar: "الألوان الهادية بتطلع جميلة — بس اختار/ي ستايلك ولونك براحتك.",
  },
  inclusionsHint: {
    en: "Inclusions may vary slightly by venue timing.",
    ar: "قد تختلف التفاصيل البسيطة حسب توقيت المكان.",
  },
  ceremony: {
    title: { en: "Church Ceremony", ar: "مراسم الكنيسة" },
    time: "5:00 PM",
    placeName: { en: "St. Mary Garden City Church", ar: "كنيسة السيدة العذراء مريم - جاردن سيتي" },
    address: { en: "1 El Saraya Al Kobra, Qasr El Nil", ar: "١ السرايا الكبرى، قصر النيل" },
    city: { en: "Cairo, Egypt", ar: "القاهرة، مصر" },
    mapUrl: "https://maps.app.goo.gl/5EFSQsZmpKwL1DRF9",
  },
  celebration: {
    title: { en: "Celebration & Dinner", ar: "الاحتفال والعشاء" },
    time: "8:00 PM",
    placeName: { en: "Air Force House – Eagles Club", ar: "دار القوات الجوية - نادي النسور" },
    address: { en: "Sheraton Al Matar, El Nozha", ar: "شيراتون المطار، النزهة" },
    city: { en: "Cairo, Egypt", ar: "القاهرة، مصر" },
    mapUrl: "https://maps.app.goo.gl/huM1dyGfxktYi31M6",
  },
  rsvp: {
    whatsappUrl: "https://wa.me/201202984272",
    whatsappUrl2: "https://wa.me/201278079756",
    whatsappUrl3: "https://wa.me/201228272513",
    phone: "+20 1202984272",
    phone2: "+20 1278079756",
    phone3: "+20 1228272513",
    contactName: { en: "Mark Yasser (Groom Brother)", ar: "(اخو العريس) مارك ياسر" },
    contactName2: { en: "Clara Ashraf (Groom Mate)", ar: "(رفيقة العريس) كلارا اشرف" },
    contactName3: { en: "Peter Hady (Groom Mate)", ar: "(رفيق العريس) بيتر هادي" },
    ctaWhatsapp: { en: "WhatsApp", ar: "واتساب" },
    ctaCall: { en: "Call", ar: "اتصال" },
    contactLabel: { en: "Contact", ar: "للتواصل" },
    /* NEW: pre-filled WhatsApp message so guests just hit send */
    waMessage: {
      en: "Hello! I'd like to ask for more details about Youssef & Sandra's wedding on 10/10/2026 🤍",
      ar: "مرحبًا! أود الاستفسار عن المزيد من التفاصيل حول حفل زفاف يوسف وساندرا يوم ١٠/١٠/٢٠٢٦ 🤍",
    },
  },
  restrictions: [
    {
      icon: "no_kids",
      title: { en: "Adults-only celebration", ar: "الاحتفال للكبار فقط" },
      desc: {
        en: "We love your little ones, but we kindly request an adults-only evening.",
        ar: "بنحب الأطفال، لكن بنعتذر: الاحتفال للكبار فقط.",
      },
    },
  ],
  inclusions: [
    { icon: "parking", title: { en: "Valet & Parking", ar: "موقف سيارات" }, desc: { en: "Parking is available at the venue for all guests.", ar: "يوجد موقف سيارات متاح لجميع الضيوف." } },
    { icon: "music", title: { en: "Live Band & DJ", ar: "موسيقى لايف و DJ" }, desc: { en: "Live music followed by a DJ for the dance floor.", ar: "استمتعوا بموسيقى لايف وبعدها DJ للرقص." } },
    { icon: "dancing", title: { en: "Dance Floor", ar: "حلبة رقص" }, desc: { en: "Dance the night away with great music and celebration.", ar: "ارقصوا واستمتعوا بالاحتفال مع موسيقى رائعة." } },
    { icon: "drinks", title: { en: "Refreshments", ar: "مشروبات" }, desc: { en: "Soft drinks and refreshments are included.", ar: "المشروبات الغازية والمرطبات مشمولة." } },
    { icon: "food", title: { en: "Dinner & Desserts", ar: "عشاء وحلويات" }, desc: { en: "A full dinner with desserts will be served during the celebration.", ar: "سيتم تقديم عشاء كامل مع حلويات أثناء الاحتفال." } },
  ],
};

/* HELPERS */
function IconFromKey({ k, size = 18 }) {
  if (k === "no_kids") return <Ban size={size} />;
  if (k === "no_photos") return <Ban size={size} />;
  if (k === "food") return <Utensils size={size} />;
  if (k === "music") return <Music size={size} />;
  if (k === "drinks") return <GlassWater size={size} />;
  if (k === "parking") return <Car size={size} />;
  if (k === "dancing") return <PartyPopper size={size} />;
  return <Sparkles size={size} />;
}

/* NEW: locale-aware digits (١٠ in Arabic, 10 in English) */
function fmtNum(n, isAR, pad = false) {
  return new Intl.NumberFormat(
    isAR ? "ar-EG" : "en-US",
    pad ? { minimumIntegerDigits: 2, useGrouping: false } : { useGrouping: false }
  ).format(n);
}

/* NEW: .ics / Google Calendar helpers */
function toICSDate(iso) {
  const d = new Date(iso);
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getUTCFullYear()}${p(d.getUTCMonth() + 1)}${p(d.getUTCDate())}T${p(d.getUTCHours())}${p(d.getUTCMinutes())}00Z`;
}

function icsEscape(s) {
  return String(s)
    .replace(/\\/g, "\\\\")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,")
    .replace(/\n/g, "\\n");
}

function downloadICS({ title, description, location, startISO, endISO }) {
  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Youssef and Sandra Wedding//EN",
    "BEGIN:VEVENT",
    `UID:${Date.now()}@wedding-invite`,
    `DTSTAMP:${toICSDate(new Date().toISOString())}`,
    `DTSTART:${toICSDate(startISO)}`,
    `DTEND:${toICSDate(endISO)}`,
    `SUMMARY:${icsEscape(title)}`,
    `DESCRIPTION:${icsEscape(description)}`,
    `LOCATION:${icsEscape(location)}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n");
  const blob = new Blob([lines], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "Youssef-Sandra-Wedding.ics";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/* SPLIT REVEAL */
function SplitReveal({ text, delay = 0, className = "" }) {
  const words = String(text).split(" ");
  return (
    <span className={`splitReveal ${className}`}>
      {words.map((w, i) => (
        <span key={i} className="splitWordWrap">
          <motion.span
            className="splitWord"
            initial={{ y: "100%", opacity: 0 }}
            animate={{ y: "0%", opacity: 1 }}
            transition={{ duration: 0.9, delay: delay + i * 0.08, ease: [0.22, 1, 0.36, 1] }}
          >
            {w}
            {i < words.length - 1 ? "\u00A0" : ""}
          </motion.span>
        </span>
      ))}
    </span>
  );
}

/* ORNAMENTAL DIVIDER */
function Ornament({ width = 140, animate = false, delay = 0 }) {
  const reduce = useReducedMotion();
  const shouldAnimate = animate && !reduce;
  return (
    <svg width={width} height="22" viewBox="0 0 140 22" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: "block", margin: "0 auto" }}>
      <motion.path
        d="M2 11 L52 11 M88 11 L138 11"
        stroke="currentColor" strokeWidth="0.7" strokeLinecap="round"
        initial={shouldAnimate ? { pathLength: 0, opacity: 0 } : false}
        animate={shouldAnimate ? { pathLength: 1, opacity: 1 } : false}
        transition={{ duration: 1.4, delay, ease: "easeInOut" }}
      />
      <motion.circle cx="58" cy="11" r="1" fill="currentColor"
        initial={shouldAnimate ? { scale: 0 } : false}
        animate={shouldAnimate ? { scale: 1 } : false}
        transition={{ duration: 0.4, delay: delay + 0.9 }} />
      <motion.circle cx="82" cy="11" r="1" fill="currentColor"
        initial={shouldAnimate ? { scale: 0 } : false}
        animate={shouldAnimate ? { scale: 1 } : false}
        transition={{ duration: 0.4, delay: delay + 0.9 }} />
      <motion.path
        d="M65 11 Q70 5 75 11 Q70 17 65 11 Z"
        fill="currentColor" opacity="0.9"
        initial={shouldAnimate ? { scale: 0, opacity: 0 } : false}
        animate={shouldAnimate ? { scale: 1, opacity: 0.9 } : false}
        transition={{ duration: 0.6, delay: delay + 1.1, type: "spring" }}
        style={{ transformOrigin: "70px 11px" }}
      />
    </svg>
  );
}

/* FALLING PETALS */
function FallingPetals() {
  const reduce = useReducedMotion();
  const items = useMemo(
    () =>
      Array.from({ length: 14 }).map((_, i) => ({
        i,
        left: Math.random() * 100,
        delay: Math.random() * 10,
        dur: 18 + Math.random() * 14,
        scale: 0.4 + Math.random() * 0.7,
        opacity: 0.08 + Math.random() * 0.18,
        drift: -50 + Math.random() * 100,
        rotate: Math.random() * 360,
        type: i % 4 === 0 ? "sparkle" : "petal",
      })),
    []
  );
  if (reduce) return null;
  return (
    <div className="fallingLayer" aria-hidden="true">
      {items.map((s) => (
        <motion.div
          key={s.i}
          className="petal"
          style={{ left: `${s.left}%`, opacity: s.opacity }}
          initial={{ y: "-10vh", x: 0, rotate: s.rotate }}
          animate={{
            y: ["0vh", "115vh"],
            x: [0, s.drift, -s.drift / 2, s.drift / 3],
            rotate: [s.rotate, s.rotate + 180, s.rotate + 360],
          }}
          transition={{
            duration: s.dur,
            delay: s.delay,
            repeat: Infinity,
            ease: "linear",
            x: { duration: s.dur, repeat: Infinity, ease: "easeInOut" },
          }}
        >
          <div style={{ transform: `scale(${s.scale})` }}>
            {s.type === "sparkle" ? (
              <Sparkles size={14} className="petalGold" />
            ) : (
              <svg width="16" height="16" viewBox="0 0 16 16" className="petalRose">
                <path d="M8 1 C 11 4, 14 6, 8 15 C 2 6, 5 4, 8 1 Z" fill="currentColor" />
              </svg>
            )}
          </div>
        </motion.div>
      ))}
    </div>
  );
}

/* FILM GRAIN */
function FilmGrain() {
  return (
    <svg className="filmGrain" aria-hidden="true">
      <filter id="noiseFilter">
        <feTurbulence type="fractalNoise" baseFrequency="0.85" numOctaves="2" stitchTiles="stitch" />
        <feColorMatrix type="saturate" values="0" />
      </filter>
      <rect width="100%" height="100%" filter="url(#noiseFilter)" />
    </svg>
  );
}

/* MAGNETIC BUTTON */
function MagneticButton({ children, className = "", as = "a", ...props }) {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 200, damping: 18 });
  const sy = useSpring(y, { stiffness: 200, damping: 18 });

  const onMove = (e) => {
    if (reduce || !ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const mx = e.clientX - (rect.left + rect.width / 2);
    const my = e.clientY - (rect.top + rect.height / 2);
    x.set(mx * 0.25);
    y.set(my * 0.25);
  };
  const onLeave = () => {
    x.set(0);
    y.set(0);
  };

  const Comp = as === "a" ? motion.a : motion.button;
  return (
    <Comp
      ref={ref}
      className={className}
      style={{ x: sx, y: sy }}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      whileTap={{ scale: 0.96 }}
      {...props}
    >
      {children}
    </Comp>
  );
}

/* SECTION */
function Section({ title, eyebrow, icon: Icon, children, delay = 0 }) {
  return (
    <motion.section
      className="card"
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      <div className="cardGlow" aria-hidden="true" />
      <motion.div
        className="cardHead"
        initial={{ opacity: 0, y: -8 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: delay + 0.15 }}
      >
        {eyebrow && <div className="eyebrow">{eyebrow}</div>}
        <div className="cardHeadRow">
          {Icon && (
            <motion.div
              className="cardIcon"
              whileHover={{ rotate: [0, -8, 8, 0], scale: 1.06 }}
              transition={{ duration: 0.5 }}
            >
              <Icon size={16} />
            </motion.div>
          )}
          <h2 className="cardTitle">{title}</h2>
        </div>
      </motion.div>
      <motion.div
        className="ornamentWrap"
        initial={{ opacity: 0, scaleX: 0 }}
        whileInView={{ opacity: 0.85, scaleX: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.9, delay: delay + 0.25 }}
      >
        <Ornament width={110} />
      </motion.div>
      <div className="cardBody">{children}</div>
    </motion.section>
  );
}

/* PLACE CARD */
function PlaceCard({ index, title, time, placeName, address, city, mapUrl, kindIcon: KindIcon, mapsLabel }) {
  return (
    <motion.div
      className="place"
      whileHover={{ y: -4 }}
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
    >
      <div className="placeTop">
        <div className="placeLeft">
          <motion.div
            className="placeKind"
            whileHover={{ rotate: [0, -8, 8, 0] }}
            transition={{ duration: 0.5 }}
          >
            <KindIcon size={20} />
          </motion.div>
          <div>
            <div className="placeTitle">{title}</div>
            <div className="placeMeta">
              <Clock size={13} /> {time}
            </div>
          </div>
        </div>
        <MagneticButton
          className="miniBtn"
          href={mapUrl}
          target="_blank"
          rel="noreferrer"
        >
          <MapPin size={14} />
          <span>{mapsLabel}</span>
        </MagneticButton>
      </div>
      <div className="placeLine">
        <div className="placeName">{placeName}</div>
        <div className="placeAddr">{address}</div>
        <div className="placeCity">{city}</div>
      </div>
    </motion.div>
  );
}

/* COUNTDOWN */
function Countdown({ isAR }) {
  const target = useMemo(() => new Date(EVENT.startISO).getTime(), []);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const diff = Math.max(0, target - now);
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((diff / (1000 * 60)) % 60);
  const seconds = Math.floor((diff / 1000) % 60);

  /* NEW: celebratory state once the day arrives */
  if (diff === 0) {
    return (
      <div className="countdown cdDone">
        <Heart size={15} fill="currentColor" />
        <span>{isAR ? "اليوم هو اليوم الكبير!" : "It's the big day!"}</span>
        <Heart size={15} fill="currentColor" />
      </div>
    );
  }

  const labels = isAR
    ? { d: "يوم", h: "ساعة", m: "دقيقة", s: "ثانية" }
    : { d: "Days", h: "Hours", m: "Minutes", s: "Seconds" };

  const cells = [
    { v: days, l: labels.d },
    { v: hours, l: labels.h },
    { v: minutes, l: labels.m },
    { v: seconds, l: labels.s },
  ];

  return (
    <div className="countdown">
      {cells.map((c, i) => (
        <React.Fragment key={i}>
          <div className="cdCell">
            <AnimatePresence mode="popLayout">
              <motion.div
                key={c.v}
                className="cdNum"
                initial={{ y: -12, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 12, opacity: 0 }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              >
                {fmtNum(c.v, isAR, true)}
              </motion.div>
            </AnimatePresence>
            <div className="cdLabel">{c.l}</div>
          </div>
          {i < cells.length - 1 && <div className="cdSep" aria-hidden="true">·</div>}
        </React.Fragment>
      ))}
    </div>
  );
}

/* MAIN */
export default function Second() {
  const [lang, setLang] = useState("en");
  const isAR = lang === "ar";
  const reduce = useReducedMotion();

  const { scrollY } = useScroll();
  const heroParallax = useTransform(scrollY, [0, 800], [0, 180]);
  const heroScale = useTransform(scrollY, [0, 800], [1.15, 1.32]);
  const heroOpacity = useTransform(scrollY, [0, 600], [1, 0.25]);
  const heroContentY = useTransform(scrollY, [0, 600], [0, -90]);
  const heroBlur = useTransform(scrollY, [0, 600], [0, 8]);
  const heroFilter = useTransform(heroBlur, (v) => `blur(${v}px)`);

  const t = (objOrString) => {
    if (objOrString == null) return "";
    if (typeof objOrString === "string") return objOrString;
    if (typeof objOrString === "object" && (objOrString.en || objOrString.ar))
      return objOrString[lang] || objOrString.en || objOrString.ar;
    return String(objOrString);
  };

  /* NEW: browser tab title follows the language */
  useEffect(() => {
    document.title = isAR ? "يوسف و ساندرا — دعوة زفاف" : "Youssef & Sandra — Wedding Invitation";
  }, [isAR]);

  /* NEW: calendar links + pre-filled WhatsApp */
  const calTitle = isAR ? "زفاف يوسف وساندرا" : "Youssef & Sandra — Wedding";
  const calDetails = [
    `${t(WEDDING.ceremony.title)} ${WEDDING.ceremony.time} — ${t(WEDDING.ceremony.placeName)}`,
    `${t(WEDDING.celebration.title)} ${WEDDING.celebration.time} — ${t(WEDDING.celebration.placeName)}`,
    `${t(WEDDING.rsvp.contactLabel)}: ${t(WEDDING.rsvp.contactName)} ${WEDDING.rsvp.phone}`,
  ].join("\n");
  const calLocation = `${t(WEDDING.ceremony.placeName)}, ${t(WEDDING.ceremony.city)}`;
  const gcalHref =
    "https://calendar.google.com/calendar/render?action=TEMPLATE" +
    `&text=${encodeURIComponent(calTitle)}` +
    `&dates=${toICSDate(EVENT.startISO)}/${toICSDate(EVENT.endISO)}` +
    `&details=${encodeURIComponent(calDetails)}` +
    `&location=${encodeURIComponent(calLocation)}`;
  const handleDownloadICS = () =>
    downloadICS({
      title: calTitle,
      description: calDetails,
      location: calLocation,
      startISO: EVENT.startISO,
      endISO: EVENT.endISO,
    });
  const waHref = `${WEDDING.rsvp.whatsappUrl}?text=${encodeURIComponent(t(WEDDING.rsvp.waMessage))}`;
  // const waHref2 = `${WEDDING.rsvp.whatsappUrl2}?text=${encodeURIComponent(t(WEDDING.rsvp.waMessage))}`;
  const waHref3 = `${WEDDING.rsvp.whatsappUrl3}?text=${encodeURIComponent(t(WEDDING.rsvp.waMessage))}`;

  const timelineRows = [
    { time: WEDDING.ceremony.time, title: WEDDING.timelineLabels.ceremony, sub: WEDDING.ceremony.placeName },
    { time: "5:45 PM", title: WEDDING.timelineLabels.photos, sub: WEDDING.timelineLabels.insideChurch },
    { time: WEDDING.celebration.time, title: WEDDING.timelineLabels.celebration, sub: WEDDING.celebration.placeName },
    { time: WEDDING.celebrationEndTime, title: WEDDING.timelineLabels.end, sub: null },
  ];

  return (
    <div className={`page ${isAR ? "rtl" : "ltr"}`} dir={isAR ? "rtl" : "ltr"} lang={lang}>
      <style>{css}</style>

      <FilmGrain />
      <FallingPetals />

      <motion.div
        className="langToggle"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.3 }}
      >
        <button className={`pill ${lang === "en" ? "active" : ""}`} onClick={() => setLang("en")} type="button" aria-pressed={lang === "en"}>EN</button>
        <button className={`pill ${lang === "ar" ? "active" : ""}`} onClick={() => setLang("ar")} type="button" aria-pressed={lang === "ar"}>عربي</button>
      </motion.div>

      <motion.section className="hero" style={{ opacity: heroOpacity }}>
        <motion.div
          className="heroBg"
          aria-hidden="true"
          initial={{ scale: 1.4, opacity: 0 }}
          animate={{ scale: 1.15, opacity: 1 }}
          transition={{ duration: 3, ease: [0.22, 1, 0.36, 1] }}
          style={{ y: heroParallax, scale: heroScale, filter: heroFilter }}
        >
          <img src={heroSection} alt="" loading="eager" decoding="async" />
        </motion.div>

        <div className="heroTone" aria-hidden="true" />
        <div className="heroVignette" aria-hidden="true" />

        {!reduce && (
          <>
            <motion.div className="lightRay lightRay1" animate={{ opacity: [0.15, 0.4, 0.15], rotate: [-12, -10, -12] }} transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }} />
            <motion.div className="lightRay lightRay2" animate={{ opacity: [0.1, 0.3, 0.1], rotate: [15, 13, 15] }} transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 2 }} />
          </>
        )}

        <motion.div className="heroContent" style={{ y: heroContentY }}>
          <motion.div
            className="heroEyebrow"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.6 }}
          >
            <span className="dot" />
            <span>{isAR ? "بدعوة من العائلتين" : "Together with their families"}</span>
            <span className="dot" />
          </motion.div>

          <h1 className="heroNames">
            <SplitReveal text={t(WEDDING.couple.groom)} delay={1.0} className={isAR ? "arName" : ""} />
            <motion.span
              className="heroAmp"
              initial={{ opacity: 0, scale: 0.4, rotate: -45 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{ delay: 1.7, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            >
              &amp;
            </motion.span>
            <SplitReveal text={t(WEDDING.couple.bride)} delay={2.0} className={isAR ? "arName" : ""} />
          </h1>

          <motion.div
            className="heroOrnamentRow"
            initial={{ opacity: 0, scaleX: 0 }}
            animate={{ opacity: 1, scaleX: 1 }}
            transition={{ duration: 1.1, delay: 2.7 }}
          >
            <Ornament width={180} animate delay={2.8} />
          </motion.div>

          <motion.div
            className="heroDate"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 3.0, duration: 0.8 }}
          >
            <div className="heroDateMain">{t(WEDDING.date)}</div>
            <div className="heroDateSub">
              <span>{t(WEDDING.dayLabel)}</span>
              <span className="heroDateDot">·</span>
              <span>{t(WEDDING.heroSubtitle)}</span>
            </div>
          </motion.div>

          <motion.div
            className="scrollCue"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 3.6, duration: 1 }}
          >
            <span className="scrollText">{isAR ? "اسحب للأسفل" : "Scroll"}</span>
            <motion.div animate={{ y: [0, 6, 0] }} transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}>
              <ChevronDown size={16} />
            </motion.div>
          </motion.div>
        </motion.div>
      </motion.section>

      <main className="wrap">
        <motion.div
          className="verse"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
        >
          <span className="verseQuote">&ldquo;</span>
          <p className="verseText">{t(WEDDING.aya)}</p>
        </motion.div>

        <Section
          eyebrow={isAR ? "عدّ تنازلي" : "Counting down to forever"}
          title={t(WEDDING.calendar.cal)}
          icon={CalendarDays}
        >
          <Countdown isAR={isAR} />

          <div className="romanticCalendar">
            <div className="calMonth">{t(WEDDING.calendar.month)}</div>
            <div className="calWeek">
              {t(WEDDING.calendar.days).map((day, i) => (
                <span key={i}>{day}</span>
              ))}
            </div>
            <div className="calDays">
              <span /><span /><span /><span />
              {Array.from({ length: 31 }, (_, i) => i + 1).map((day, idx) => (
                <motion.span
                  key={day}
                  className={day === 10 ? "calDay heartDay" : "calDay"}
                  initial={{ opacity: 0, scale: 0.6 }}
                  whileInView={{ opacity: 1, scale: day === 10 ? 1.05 : 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: idx * 0.012 }}
                >
                  {day === 10 ? (
                    <>
                      <Heart size={12} fill="currentColor" className="heartIcon" />
                      <span className="heartDayNum">{fmtNum(day, isAR)}</span>
                    </>
                  ) : (
                    fmtNum(day, isAR)
                  )}
                </motion.span>
              ))}
            </div>
            <div className="calCaption">
              {isAR ? "نتمنى لقاءكم في يومنا المميز" : "We can't wait to celebrate with you"}
            </div>
          </div>

          {/* NEW: one-tap "add to calendar" */}
          <div className="calBtns">
            <MagneticButton
              className="miniBtn"
              href={gcalHref}
              target="_blank"
              rel="noreferrer"
            >
              <CalendarPlus size={14} />
              <span>{t(WEDDING.calendarCta.google)}</span>
            </MagneticButton>
            <MagneticButton
              as="button"
              type="button"
              className="miniBtn"
              onClick={handleDownloadICS}
            >
              <CalendarPlus size={14} />
              <span>{t(WEDDING.calendarCta.ics)}</span>
            </MagneticButton>
          </div>
        </Section>

        <Section
          eyebrow={isAR ? "البرنامج" : "Where it happens"}
          title={t(WEDDING.sections.dayPlan)}
          icon={CalendarDays}
        >
          <PlaceCard
            index={1}
            title={t(WEDDING.ceremony.title)}
            time={WEDDING.ceremony.time}
            placeName={t(WEDDING.ceremony.placeName)}
            address={t(WEDDING.ceremony.address)}
            city={t(WEDDING.ceremony.city)}
            mapUrl={WEDDING.ceremony.mapUrl}
            kindIcon={Church}
            mapsLabel={isAR ? "الخريطة" : "Maps"}
          />
          <div className="dividerOrnate">
            <span className="dividerLine" />
            <motion.div
              animate={{ scale: [1, 1.25, 1], rotate: [0, 8, -8, 0] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              className="dividerHeartWrap"
            >
              <Heart size={11} fill="currentColor" className="dividerHeart" />
            </motion.div>
            <span className="dividerLine" />
          </div>
          {/* FIX: the celebration venue was never rendered */}
          <PlaceCard
            index={2}
            title={t(WEDDING.celebration.title)}
            time={WEDDING.celebration.time}
            placeName={t(WEDDING.celebration.placeName)}
            address={t(WEDDING.celebration.address)}
            city={t(WEDDING.celebration.city)}
            mapUrl={WEDDING.celebration.mapUrl}
            kindIcon={PartyPopper}
            mapsLabel={isAR ? "الخريطة" : "Maps"}
          />
        </Section>

        <Section
          eyebrow={isAR ? "تفاصيل اليوم" : "Hour by hour"}
          title={t(WEDDING.sections.timeline)}
          icon={Clock}
        >
          <div className="timeline">
            <motion.div
              className="timelineRail"
              initial={{ scaleY: 0 }}
              whileInView={{ scaleY: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1.4, ease: "easeInOut" }}
              style={{ transformOrigin: "top" }}
            />
            {timelineRows.map((row, idx) => (
              <motion.div
                key={idx}
                className="tRow"
                initial={{ opacity: 0, x: -24 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.6, delay: idx * 0.15 }}
              >
                <div className="tTime">{row.time}</div>
                <motion.div
                  className="tDot"
                  initial={{ scale: 0 }}
                  whileInView={{ scale: [0, 1.4, 1] }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7, delay: idx * 0.15 + 0.3 }}
                />
                <div className="tText">
                  <div className="tTitle">{t(row.title)}</div>
                  {row.sub && <div className="tSub">{t(row.sub)}</div>}
                </div>
              </motion.div>
            ))}
          </div>
        </Section>

        <Section
          eyebrow={isAR ? "الستايل" : "What to wear"}
          title={t(WEDDING.sections.dressCode)}
          icon={Sparkles}
        >
          <motion.div
            className="kv"
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="k">{isAR ? "الستايل" : "Style"}</div>
            <div className="v">{t(WEDDING.dressCode)}</div>
          </motion.div>
          <div className="hint">{t(WEDDING.dressHint)}</div>
        </Section>

        {/* FIX: "What to Expect" existed in the data but was never rendered */}
        <Section
          eyebrow={isAR ? "ليلة لا تُنسى" : "An evening to remember"}
          title={t(WEDDING.sections.inclusions)}
          icon={PartyPopper}
        >
          <div className="inclusions">
            {WEDDING.inclusions.map((item, i) => (
              <motion.div
                key={i}
                className="iItem"
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-20px" }}
                transition={{ duration: 0.55, delay: i * 0.07 }}
              >
                <div className="iIcon"><IconFromKey k={item.icon} /></div>
                <div>
                  <div className="iTitle">{t(item.title)}</div>
                  <div className="iDesc">{t(item.desc)}</div>
                </div>
              </motion.div>
            ))}
          </div>
          <div className="hint">{t(WEDDING.inclusionsHint)}</div>
        </Section>

        {/* FIX: "Kindly Note" (adults-only) existed in the data but was never rendered */}
        <Section
          eyebrow={isAR ? "لراحة الجميع" : "A gentle note"}
          title={t(WEDDING.sections.restrictions)}
          icon={Heart}
        >
          <div className="restrictions">
            {WEDDING.restrictions.map((item, i) => (
              <motion.div
                key={i}
                className="rItem"
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-20px" }}
                transition={{ duration: 0.55, delay: i * 0.07 }}
              >
                <div className="rIcon"><IconFromKey k={item.icon} /></div>
                <div>
                  <div className="rTitle">{t(item.title)}</div>
                  <div className="rDesc">{t(item.desc)}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </Section>

        <Section
          // eyebrow={isAR ? "تأكيد الحضور" : "Reply to attend"}
          title={t(WEDDING.sections.contact)}
          icon={Send}
        >
          <div className="fine">
            {t(WEDDING.rsvp.contactLabel)} — {t(WEDDING.rsvp.contactName)}
          </div>
          <div className="btnRow">
            <MagneticButton
              className="btn"
              href={waHref}
              target="_blank"
              rel="noreferrer"
            >
              <Send size={16} />
              <span>{t(WEDDING.rsvp.ctaWhatsapp)}</span>
            </MagneticButton>
            <MagneticButton
              className="btn ghost"
              href={`tel:${WEDDING.rsvp.phone}`}
            >
              <Phone size={16} />
              <span>{t(WEDDING.rsvp.ctaCall)}</span>
            </MagneticButton>
          </div>
          <div className="fine">
            {t(WEDDING.rsvp.contactLabel)} — {t(WEDDING.rsvp.contactName3)}
          </div>
          <div className="btnRow">
            <MagneticButton
              className="btn"
              href={waHref3}
              target="_blank"
              rel="noreferrer"
            >
              <Send size={16} />
              <span>{t(WEDDING.rsvp.ctaWhatsapp)}</span>
            </MagneticButton>
            <MagneticButton
              className="btn ghost"
              href={`tel:${WEDDING.rsvp.phone3}`}
            >
              <Phone size={16} />
              <span>{t(WEDDING.rsvp.ctaCall)}</span>
            </MagneticButton>
          </div>
          
        </Section>

        <motion.footer
          className="footer"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
        >
          <Ornament width={90} />
          <div className="footerText">
            {isAR ? "صُنع بحب لـ" : "Made with love for"}{" "}
            <span className="footerNames">
              {t(WEDDING.couple.groom)} &amp; {t(WEDDING.couple.bride)}
            </span>
          </div>
          <div className="footerDate">{t(WEDDING.dateLong)}</div>
        </motion.footer>
      </main>
    </div>
  );
}

/* ───────────────────────────────────────────────────────────
   STYLES
   ─────────────────────────────────────────────────────────── */
const css = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;0,700;1,300;1,400&family=Italiana&family=Playfair+Display:ital,wght@0,400;0,500;0,600;0,700;1,400&family=Inter:wght@300;400;500;600&family=Amiri:ital,wght@0,400;0,700;1,400&family=Reem+Kufi:wght@400;500;600;700&display=swap');

  :root{
    /* Moody burgundy / blush / gold */
    --bg-1: #1a0e10;
    --bg-2: #241317;
    --bg-3: #2c1519;

    --burgundy: #5b1f24;
    --burgundy-deep: #3d1418;
    --wine: #7a2a30;
    --blush: #d8a8a0;
    --blush-soft: #e8c5be;
    --champagne: #e9d3b2;

    --gold: #c9a36b;
    --gold-light: #e2c896;
    --gold-deep: #9c7a45;
    --gold-glow: rgba(201, 163, 107, 0.5);

    --ink: #f5ead6;
    --ink-soft: rgba(245, 234, 214, 0.75);
    --ink-faint: rgba(245, 234, 214, 0.45);

    --glass: rgba(245, 234, 214, 0.04);
    --glass-strong: rgba(245, 234, 214, 0.07);
    --stroke: rgba(201, 163, 107, 0.25);
    --stroke-soft: rgba(201, 163, 107, 0.12);

    --serif: 'Cormorant Garamond', 'Times New Roman', serif;
    --display: 'Italiana', 'Playfair Display', serif;
    --sans: 'Inter', system-ui, sans-serif;
    --arabic: 'Amiri', 'Reem Kufi', serif;
    --arabic-display: 'Reem Kufi', 'Amiri', serif;
  }

  *{ box-sizing: border-box; }

  html, body{ margin: 0; padding: 0; }

  .page{
    min-height: 100vh;
    background:
      radial-gradient(900px 700px at 15% 10%, rgba(122, 42, 48, 0.35), transparent 55%),
      radial-gradient(700px 600px at 85% 30%, rgba(91, 31, 36, 0.30), transparent 60%),
      radial-gradient(600px 500px at 50% 95%, rgba(201, 163, 107, 0.10), transparent 65%),
      linear-gradient(180deg, var(--bg-1) 0%, var(--bg-2) 50%, var(--bg-3) 100%);
    color: var(--ink);
    position: relative;
    overflow-x: hidden;
    font-family: var(--serif);
    font-weight: 400;
  }
  .page.rtl{ font-family: var(--arabic); }

  /* Film grain */
  .filmGrain{
    position: fixed;
    inset: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    opacity: 0.06;
    mix-blend-mode: overlay;
    z-index: 999;
  }

  /* Falling petals */
  .fallingLayer{
    position: fixed;
    inset: 0;
    pointer-events: none;
    z-index: 1;
    overflow: hidden;
  }
  .petal{ position: absolute; top: -40px; }
  .petalGold{ color: var(--gold); }
  .petalRose{ color: var(--blush); }

  /* Language toggle */
  .langToggle{
    position: fixed;
    top: 18px;
    right: 18px;
    z-index: 100;
    display: flex;
    gap: 4px;
    padding: 5px;
    border-radius: 999px;
    background: rgba(26, 14, 16, 0.6);
    border: 1px solid var(--stroke);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.05);
    backdrop-filter: blur(14px) saturate(120%);
    -webkit-backdrop-filter: blur(14px) saturate(120%);
  }
  .page.rtl .langToggle{ right: auto; left: 18px; }
  .pill{
    border: 0;
    cursor: pointer;
    padding: 7px 14px;
    border-radius: 999px;
    font-weight: 500;
    font-size: 11px;
    letter-spacing: 0.2em;
    background: transparent;
    color: var(--ink-soft);
    font-family: var(--sans);
    transition: all 0.4s cubic-bezier(0.22, 1, 0.36, 1);
  }
  .pill.active{
    background: linear-gradient(135deg, var(--gold-light), var(--gold));
    color: var(--bg-1);
    box-shadow: 0 4px 14px rgba(201, 163, 107, 0.35);
  }

  /* Ornaments */
  .ornamentWrap{
    display: flex;
    justify-content: center;
    color: var(--gold);
    margin: 14px 0;
    opacity: 0.85;
  }

  /* ═══════════════════════════════════════════════════════
     HERO
     ═══════════════════════════════════════════════════════ */
  .hero{
    position: relative;
    min-height: 100vh;
    min-height: 100svh; /* NEW: stable height on mobile browser chrome */
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    will-change: opacity;
  }
  .heroBg{
    position: absolute;
    inset: -10%;
    z-index: 0;
    will-change: transform, filter;
  }
  .heroBg img{
    width: 100%;
    height: 100%;
    object-fit: cover;
    filter: brightness(0.55) saturate(1.1) contrast(1.05);
  }
  .heroTone{
    position: absolute;
    inset: 0;
    z-index: 1;
    background:
      radial-gradient(ellipse at 50% 50%, transparent 0%, rgba(26, 14, 16, 0.4) 60%, rgba(26, 14, 16, 0.85) 100%),
      linear-gradient(180deg, rgba(26, 14, 16, 0.45) 0%, transparent 30%, transparent 70%, rgba(26, 14, 16, 0.95) 100%),
      linear-gradient(135deg, rgba(91, 31, 36, 0.35), transparent 60%);
  }
  .heroVignette{
    position: absolute;
    inset: 0;
    z-index: 2;
    pointer-events: none;
    box-shadow:
      inset 0 0 200px 60px rgba(0, 0, 0, 0.55),
      inset 0 0 120px rgba(0, 0, 0, 0.4);
  }
  .lightRay{
    position: absolute;
    width: 200%;
    height: 60%;
    z-index: 2;
    pointer-events: none;
    mix-blend-mode: screen;
  }
  .lightRay1{
    top: -10%;
    left: -50%;
    background: linear-gradient(90deg, transparent 30%, rgba(233, 211, 178, 0.18) 50%, transparent 70%);
    filter: blur(40px);
  }
  .lightRay2{
    bottom: -10%;
    right: -50%;
    background: linear-gradient(90deg, transparent 30%, rgba(216, 168, 160, 0.15) 50%, transparent 70%);
    filter: blur(50px);
  }

  .heroContent{
    position: relative;
    z-index: 3;
    text-align: center;
    padding: 60px 26px;
    max-width: 720px;
    will-change: transform;
  }

  .heroEyebrow{
    display: inline-flex;
    align-items: center;
    gap: 14px;
    font-size: 10px;
    font-family: var(--sans);
    font-weight: 500;
    letter-spacing: 0.5em;
    text-transform: uppercase;
    color: var(--gold);
    margin-bottom: 36px;
  }
  .page.rtl .heroEyebrow{
    font-family: var(--arabic);
    letter-spacing: 0.15em;
    font-size: 13px;
  }
  .heroEyebrow .dot{
    width: 4px;
    height: 4px;
    border-radius: 999px;
    background: var(--gold);
    opacity: 0.7;
  }

  /* Names — display serif, big, refined */
  .heroNames{
    font-family: var(--display);
    font-weight: 400;
    font-size: clamp(58px, 12vw, 110px);
    line-height: 1.0;
    letter-spacing: -0.01em;
    color: var(--ink);
    margin: 0;
    text-shadow: 0 4px 30px rgba(0, 0, 0, 0.5);
    display: flex;
    flex-direction: column;
    gap: 4px;
    align-items: center;
  }
  .page.rtl .heroNames{
    font-family: var(--arabic-display);
    font-size: clamp(50px, 11vw, 92px);
    font-weight: 600;
  }
  .heroAmp{
    display: inline-block;
    font-family: var(--display);
    font-style: italic;
    font-size: 0.55em;
    color: var(--gold);
    margin: 4px 0;
    font-weight: 400;
    text-shadow: 0 2px 18px rgba(201, 163, 107, 0.5);
  }
  .splitReveal{
    display: inline-block;
    overflow: hidden;
  }
  .splitWordWrap{
    display: inline-block;
    overflow: hidden;
    vertical-align: top;
  }
  .splitWord{
    display: inline-block;
  }

  .heroOrnamentRow{
    margin: 30px 0 24px;
    color: var(--gold);
  }

  .heroDate{
    margin-top: 6px;
  }
  .heroDateMain{
    font-family: var(--sans);
    font-weight: 300;
    font-size: clamp(20px, 3.5vw, 28px);
    letter-spacing: 0.4em;
    color: var(--ink);
    margin-bottom: 14px;
  }
  .page.rtl .heroDateMain{
    font-family: var(--arabic);
    letter-spacing: 0.1em;
    font-weight: 400;
  }
  .heroDateSub{
    display: inline-flex;
    align-items: center;
    gap: 12px;
    font-family: var(--sans);
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.3em;
    text-transform: uppercase;
    color: var(--ink-soft);
  }
  .page.rtl .heroDateSub{
    font-family: var(--arabic);
    letter-spacing: 0.05em;
  }
  .heroDateDot{ color: var(--gold); }

  .scrollCue{
    position: absolute;
    bottom: -100px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    color: var(--ink-faint);
  }
  .scrollText{
    font-family: var(--sans);
    font-size: 9px;
    letter-spacing: 0.4em;
    text-transform: uppercase;
    font-weight: 500;
  }
  .page.rtl .scrollText{
    font-family: var(--arabic);
    letter-spacing: 0.1em;
    font-size: 11px;
  }

  /* ═══════════════════════════════════════════════════════
     BODY WRAP
     ═══════════════════════════════════════════════════════ */
  .wrap{
    width: min(680px, 92vw);
    margin: 0 auto;
    padding: 80px 0 60px;
    display: flex;
    flex-direction: column;
    gap: 28px;
    position: relative;
    z-index: 2;
  }

  /* Verse */
  .verse{
    text-align: center;
    padding: 36px 24px 20px;
    position: relative;
  }
  .verseQuote{
    font-family: var(--display);
    font-size: 90px;
    line-height: 0.5;
    color: var(--gold);
    opacity: 0.4;
    display: block;
    margin-bottom: 8px;
    height: 30px;
  }
  .verseText{
    white-space: pre-line;
    font-family: var(--display);
    font-size: clamp(20px, 4vw, 28px);
    line-height: 1.5;
    font-style: italic;
    color: var(--ink);
    margin: 0;
    letter-spacing: 0.01em;
  }
  .page.rtl .verseText{
    font-family: var(--arabic-display);
    font-style: normal;
    font-weight: 600;
  }

  /* Cards */
  .card{
    position: relative;
    padding: 36px 26px 30px;
    border-radius: 24px;
    background: linear-gradient(160deg, rgba(245, 234, 214, 0.04), rgba(245, 234, 214, 0.015));
    border: 1px solid var(--stroke);
    box-shadow:
      0 30px 80px rgba(0, 0, 0, 0.5),
      inset 0 1px 0 rgba(255, 255, 255, 0.04);
    backdrop-filter: blur(20px) saturate(110%);
    -webkit-backdrop-filter: blur(20px) saturate(110%);
    overflow: hidden;
  }
  .cardGlow{
    position: absolute;
    top: -40%;
    left: 50%;
    width: 80%;
    height: 80%;
    transform: translateX(-50%);
    background: radial-gradient(ellipse, rgba(201, 163, 107, 0.10), transparent 60%);
    pointer-events: none;
    filter: blur(40px);
  }

  .cardHead{
    text-align: center;
    margin-bottom: 6px;
  }
  .eyebrow{
    font-family: var(--sans);
    font-size: 10px;
    font-weight: 500;
    letter-spacing: 0.4em;
    text-transform: uppercase;
    color: var(--gold);
    margin-bottom: 14px;
    opacity: 0.85;
  }
  .page.rtl .eyebrow{
    font-family: var(--arabic);
    letter-spacing: 0.1em;
    font-size: 12px;
  }
  .cardHeadRow{
    display: inline-flex;
    align-items: center;
    gap: 12px;
  }
  .cardIcon{
    width: 32px;
    height: 32px;
    border-radius: 12px;
    display: grid;
    place-items: center;
    background: linear-gradient(135deg, rgba(201, 163, 107, 0.18), rgba(201, 163, 107, 0.08));
    border: 1px solid var(--stroke);
    color: var(--gold-light);
  }
  .cardTitle{
    font-family: var(--display);
    font-size: clamp(26px, 4.5vw, 36px);
    font-weight: 400;
    letter-spacing: 0.04em;
    color: var(--ink);
    margin: 0;
    line-height: 1;
  }
  .page.rtl .cardTitle{
    font-family: var(--arabic-display);
    font-weight: 600;
  }
  .cardBody{ margin-top: 6px; }

  /* Countdown */
  .countdown{
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 20px 8px;
    margin-bottom: 18px;
    border: 1px solid var(--stroke-soft);
    border-radius: 18px;
    background: rgba(0, 0, 0, 0.2);
  }
  .cdCell{
    text-align: center;
    min-width: 56px;
    padding: 4px 6px;
  }
  .cdNum{
    font-family: var(--display);
    font-size: clamp(28px, 6vw, 38px);
    font-weight: 400;
    color: var(--ink);
    line-height: 1;
    letter-spacing: 0.02em;
  }
  .cdLabel{
    margin-top: 6px;
    font-family: var(--sans);
    font-size: 9px;
    font-weight: 500;
    letter-spacing: 0.25em;
    text-transform: uppercase;
    color: var(--ink-faint);
  }
  .page.rtl .cdLabel{
    font-family: var(--arabic);
    letter-spacing: 0.05em;
    font-size: 11px;
  }
  .cdSep{
    color: var(--gold);
    font-size: 22px;
    font-weight: 300;
    opacity: 0.4;
    padding-bottom: 16px;
  }
  /* NEW: celebratory state when the countdown hits zero */
  .cdDone{
    gap: 12px;
    font-family: var(--display);
    font-size: clamp(18px, 4.5vw, 24px);
    letter-spacing: 0.04em;
    color: var(--gold-light);
  }
  .page.rtl .cdDone{
    font-family: var(--arabic-display);
    font-weight: 600;
  }

  /* NEW: add-to-calendar buttons */
  .calBtns{
    display: flex;
    gap: 10px;
    justify-content: center;
    flex-wrap: wrap;
    margin-top: 20px;
  }

  /* Calendar */
  .romanticCalendar{
    text-align: center;
    padding: 12px 4px 8px;
  }
  .calMonth{
    font-family: var(--display);
    font-size: clamp(22px, 4vw, 28px);
    color: var(--gold-light);
    margin-bottom: 16px;
    letter-spacing: 0.15em;
    font-weight: 400;
  }
  .page.rtl .calMonth{
    font-family: var(--arabic-display);
    letter-spacing: 0.05em;
    font-weight: 600;
  }
  .calWeek{
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 4px;
    margin-bottom: 10px;
    padding-bottom: 10px;
    border-bottom: 1px solid var(--stroke-soft);
  }
  .calWeek span{
    font-family: var(--sans);
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.2em;
    color: var(--gold);
    text-transform: uppercase;
    padding: 6px 0;
    opacity: 0.7;
  }
  .page.rtl .calWeek span{
    font-family: var(--arabic);
    font-size: 13px;
  }
  .calDays{
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 4px;
  }
  .calDay{
    aspect-ratio: 1;
    display: grid;
    place-items: center;
    font-family: var(--serif);
    font-size: 14px;
    color: var(--ink-soft);
    border-radius: 12px;
    transition: all 0.4s ease;
    font-weight: 400;
    position: relative;
  }
  .page.rtl .calDay{ font-family: var(--arabic); }
  .heartDay{
    background: linear-gradient(135deg, var(--burgundy), var(--wine));
    color: var(--gold-light);
    font-weight: 600;
    box-shadow:
      0 10px 28px rgba(91, 31, 36, 0.6),
      inset 0 1px 0 rgba(255, 255, 255, 0.1),
      0 0 0 1px var(--gold);
    position: relative;
    overflow: hidden;
    animation: heartPulse 3s ease-in-out infinite;
  }
  @keyframes heartPulse{
    0%, 100% { transform: scale(1.05); box-shadow: 0 10px 28px rgba(91, 31, 36, 0.55), inset 0 1px 0 rgba(255, 255, 255, 0.1), 0 0 0 1px var(--gold); }
    50% { transform: scale(1.14); box-shadow: 0 16px 36px rgba(91, 31, 36, 0.75), inset 0 1px 0 rgba(255, 255, 255, 0.15), 0 0 0 1px var(--gold-light); }
  }
  .heartIcon{
    position: absolute;
    top: 4px;
    right: 4px;
    color: var(--gold-light);
  }
  .heartDayNum{
    font-size: 14px;
    font-weight: 700;
    font-family: var(--display);
  }
  .page.rtl .heartDayNum{ font-family: var(--arabic); }
  .calCaption{
    margin-top: 22px;
    font-family: var(--display);
    font-size: 14px;
    font-style: italic;
    color: var(--ink-faint);
    letter-spacing: 0.05em;
  }
  .page.rtl .calCaption{
    font-family: var(--arabic-display);
    font-style: normal;
  }

  /* Place Cards */
  .place{
    position: relative;
    border: 1px solid var(--stroke-soft);
    background: rgba(0, 0, 0, 0.25);
    border-radius: 20px;
    padding: 22px 18px 18px;
    overflow: hidden;
  }
  .place::before{
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, rgba(201, 163, 107, 0.05), transparent 60%);
    pointer-events: none;
  }
  .placeTop{
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    position: relative;
  }
  .placeLeft{
    display: flex;
    gap: 14px;
    align-items: flex-start;
  }
  .placeKind{
    width: 44px;
    height: 44px;
    border-radius: 14px;
    display: grid;
    place-items: center;
    background: linear-gradient(135deg, rgba(201, 163, 107, 0.20), rgba(91, 31, 36, 0.30));
    border: 1px solid var(--stroke);
    color: var(--gold-light);
    flex: 0 0 auto;
  }
  .placeTitle{
    font-family: var(--display);
    font-weight: 400;
    font-size: 19px;
    letter-spacing: 0.02em;
    color: var(--ink);
  }
  .page.rtl .placeTitle{
    font-family: var(--arabic-display);
    font-weight: 600;
  }
  .placeMeta{
    margin-top: 5px;
    font-family: var(--sans);
    font-size: 11px;
    color: var(--gold);
    display: flex;
    gap: 6px;
    align-items: center;
    font-weight: 500;
    letter-spacing: 0.15em;
    text-transform: uppercase;
  }
  .miniBtn{
    display: inline-flex;
    gap: 6px;
    align-items: center;
    justify-content: center;
    padding: 9px 14px;
    border-radius: 999px;
    border: 1px solid var(--stroke);
    background: rgba(201, 163, 107, 0.08);
    color: var(--gold-light);
    text-decoration: none;
    font-family: var(--sans);
    font-weight: 500;
    font-size: 10px;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    white-space: nowrap;
    transition: background 0.4s, color 0.4s, border-color 0.4s;
    cursor: pointer;
    will-change: transform;
  }
  .page.rtl .miniBtn{
    font-family: var(--arabic);
    letter-spacing: 0.05em;
    font-size: 12px;
  }
  .miniBtn:hover{
    background: var(--gold);
    color: var(--bg-1);
    border-color: var(--gold-light);
  }
  .placeLine{
    margin-top: 16px;
    padding-top: 14px;
    border-top: 1px dashed var(--stroke-soft);
    position: relative;
  }
  .placeName{
    font-family: var(--serif);
    font-weight: 600;
    font-size: 15px;
    color: var(--ink);
    letter-spacing: 0.01em;
  }
  .page.rtl .placeName{
    font-family: var(--arabic);
    font-weight: 700;
  }
  .placeAddr, .placeCity{
    margin-top: 4px;
    font-size: 13px;
    color: var(--ink-soft);
    line-height: 1.5;
  }

  .dividerOrnate{
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    margin: 18px 0;
    color: var(--gold);
  }
  .dividerLine{
    flex: 1;
    height: 1px;
    background: linear-gradient(90deg, transparent, var(--stroke), transparent);
  }
  .dividerHeartWrap{ display: grid; place-items: center; }

  /* Timeline */
  .timeline{
    display: flex;
    flex-direction: column;
    gap: 18px;
    position: relative;
    padding: 6px 0;
  }
  .timelineRail{
    position: absolute;
    left: 92px;
    top: 12px;
    bottom: 12px;
    width: 1px;
    background: linear-gradient(180deg, transparent, var(--gold) 15%, var(--gold) 85%, transparent);
    opacity: 0.4;
  }
  .page.rtl .timelineRail{ left: auto; right: 92px; }
  .tRow{
    display: grid;
    grid-template-columns: 80px 14px 1fr;
    gap: 14px;
    align-items: flex-start;
    position: relative;
  }
  .page.rtl .tRow{ grid-template-columns: 1fr 14px 80px; }
  .tTime{
    font-family: var(--sans);
    font-weight: 600;
    font-size: 11px;
    color: var(--gold);
    letter-spacing: 0.15em;
    padding-top: 2px;
    text-transform: uppercase;
  }
  .page.rtl .tTime{
    text-align: right;
    font-family: var(--arabic);
    letter-spacing: 0.02em;
    font-size: 13px;
  }
  .tDot{
    width: 12px;
    height: 12px;
    border-radius: 999px;
    margin-top: 5px;
    background: radial-gradient(circle at 30% 25%, var(--gold-light), var(--gold));
    box-shadow:
      0 0 0 4px var(--bg-2),
      0 0 0 5px var(--gold),
      0 6px 16px rgba(201, 163, 107, 0.5);
    position: relative;
    z-index: 1;
  }
  .tTitle{
    font-family: var(--display);
    font-weight: 400;
    font-size: 17px;
    color: var(--ink);
    letter-spacing: 0.01em;
  }
  .page.rtl .tTitle{
    font-family: var(--arabic-display);
    font-weight: 600;
  }
  .tSub{
    margin-top: 4px;
    font-size: 13px;
    color: var(--ink-soft);
    font-style: italic;
  }
  .page.rtl .tSub{ font-style: normal; }

  /* Key/Value */
  .kv{
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    border: 1px solid var(--stroke-soft);
    background: rgba(0, 0, 0, 0.2);
    padding: 16px 18px;
    border-radius: 16px;
  }
  .k{
    font-family: var(--sans);
    font-size: 10px;
    color: var(--gold);
    font-weight: 500;
    letter-spacing: 0.3em;
    text-transform: uppercase;
  }
  .page.rtl .k{
    font-family: var(--arabic);
    letter-spacing: 0.05em;
    font-size: 12px;
  }
  .v{
    font-family: var(--display);
    font-size: 18px;
    font-weight: 400;
    color: var(--ink);
    letter-spacing: 0.02em;
  }
  .page.rtl .v{
    font-family: var(--arabic-display);
    font-weight: 600;
  }

  /* Restrictions / Inclusions */
  .restrictions, .inclusions{
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  .rItem, .iItem{
    display: flex;
    gap: 14px;
    align-items: flex-start;
    border: 1px solid var(--stroke-soft);
    background: rgba(0, 0, 0, 0.2);
    padding: 16px;
    border-radius: 16px;
    transition: background 0.4s, border-color 0.4s;
  }
  .rItem:hover, .iItem:hover{
    background: rgba(201, 163, 107, 0.05);
    border-color: var(--stroke);
  }
  .rIcon, .iIcon{
    width: 38px;
    height: 38px;
    border-radius: 12px;
    display: grid;
    place-items: center;
    background: linear-gradient(135deg, rgba(201, 163, 107, 0.16), rgba(91, 31, 36, 0.20));
    border: 1px solid var(--stroke-soft);
    color: var(--gold-light);
    flex: 0 0 auto;
  }
  .rTitle, .iTitle{
    font-family: var(--display);
    font-weight: 400;
    font-size: 16px;
    color: var(--ink);
    letter-spacing: 0.01em;
  }
  .page.rtl .rTitle, .page.rtl .iTitle{
    font-family: var(--arabic-display);
    font-weight: 600;
  }
  .rDesc, .iDesc{
    margin-top: 4px;
    font-size: 13px;
    color: var(--ink-soft);
    line-height: 1.55;
  }
  /* NEW: two-column inclusions on wider screens (odd last item spans full width) */
  @media (min-width: 520px){
    .inclusions{
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 10px;
    }
    .inclusions .iItem:nth-last-child(1):nth-child(odd){ grid-column: 1 / -1; }
  }
  .hint{
    margin-top: 16px;
    font-family: var(--display);
    font-size: 13px;
    color: var(--ink-faint);
    line-height: 1.6;
    text-align: center;
    font-style: italic;
    letter-spacing: 0.02em;
  }
  .page.rtl .hint{
    font-family: var(--arabic-display);
    font-style: normal;
  }

  /* Buttons */
  .btnRow{
    display: flex;
    gap: 10px;
    margin-top: 6px;
  }
  .btn{
    flex: 1;
    display: flex;
    gap: 10px;
    align-items: center;
    justify-content: center;
    padding: 16px 18px;
    border-radius: 16px;
    border: 1px solid var(--gold);
    background: linear-gradient(135deg, var(--gold-light), var(--gold));
    color: var(--bg-1);
    text-decoration: none;
    font-family: var(--sans);
    font-weight: 600;
    font-size: 11px;
    letter-spacing: 0.25em;
    text-transform: uppercase;
    box-shadow: 0 14px 30px rgba(201, 163, 107, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.25);
    cursor: pointer;
    will-change: transform;
    transition: box-shadow 0.4s;
  }
  .page.rtl .btn{
    font-family: var(--arabic);
    letter-spacing: 0.05em;
    font-size: 14px;
  }
  .btn:hover{ box-shadow: 0 18px 40px rgba(201, 163, 107, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.3); }
  .btn.ghost{
    background: rgba(0, 0, 0, 0.2);
    color: var(--gold-light);
    border: 1px solid var(--stroke);
    box-shadow: 0 8px 18px rgba(0, 0, 0, 0.3);
  }
  .btn.ghost:hover{
    background: rgba(201, 163, 107, 0.1);
    border-color: var(--gold);
  }

  .fine{
    margin-top: 18px;
    font-family: var(--display);
    font-size: 14px;
    color: var(--ink-soft);
    text-align: center;
    font-style: italic;
    letter-spacing: 0.02em;
  }
  .page.rtl .fine{
    font-family: var(--arabic-display);
    font-style: normal;
  }

  /* Footer */
  .footer{
    text-align: center;
    padding: 30px 0 0;
    color: var(--gold);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
  }
  .footerText{
    font-family: var(--display);
    font-size: 13px;
    color: var(--ink-soft);
    font-style: italic;
    letter-spacing: 0.05em;
    margin-top: 4px;
  }
  .page.rtl .footerText{
    font-family: var(--arabic-display);
    font-style: normal;
  }
  .footerNames{
    font-style: normal;
    font-weight: 500;
    color: var(--gold-light);
  }
  .footerDate{
    font-family: var(--sans);
    font-size: 9px;
    font-weight: 500;
    letter-spacing: 0.4em;
    text-transform: uppercase;
    color: var(--ink-faint);
    margin-top: 2px;
  }
  .page.rtl .footerDate{
    font-family: var(--arabic);
    letter-spacing: 0.05em;
    font-size: 12px;
  }

  /* Responsive */
  @media (max-width: 480px){
    .heroContent{ padding: 50px 22px; }
    .heroEyebrow{ letter-spacing: 0.35em; gap: 10px; }
    .heroDateMain{ letter-spacing: 0.3em; }
    .heroDateSub{ flex-wrap: wrap; justify-content: center; gap: 8px; letter-spacing: 0.2em; }
    .card{ padding: 30px 20px 24px; }
    .countdown{ gap: 2px; }
    .cdCell{ min-width: 48px; padding: 4px 2px; }
    .tRow{ grid-template-columns: 68px 12px 1fr; gap: 10px; }
    .page.rtl .tRow{ grid-template-columns: 1fr 12px 68px; }
    .timelineRail{ left: 78px; }
    .page.rtl .timelineRail{ left: auto; right: 78px; }
  }
  @media (max-width: 360px){
    .btnRow{ flex-direction: column; }
  }

  @media (prefers-reduced-motion: reduce){
    *, *::before, *::after{
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.2s !important;
    }
  }
`;